import JetskiHero from 'assets/images/psp/PSPHero/jet-ski-hero.webp';
import SaltWater from 'assets/images/psp/Icon/salt-water.svg';
import SpaceShortage from 'assets/images/psp/Icon/space-shortage.svg';
import TheftVulnerability from 'assets/images/psp/Icon/theft-vulnerability.svg';
import UVWeather from 'assets/images/psp/Icon/uv-weather.svg';
import WinterFreeze from 'assets/images/psp/Icon/winter-freeze.svg';
import Maintenance from 'assets/images/psp/Icon/maintenance.svg';
import ProperVentilation from 'assets/images/psp/Icon/proper-ventilation.svg';
import InsurancePeace from 'assets/images/psp/Icon/insurance-peace.svg';
import BasicOutdoorStorage from 'assets/images/psp/Icon/basic-outdoor-storage.svg';
import CoveredStorage from 'assets/images/psp/Icon/covered-storage.svg';
import IndoorStorage from 'assets/images/psp/Icon/indoor-storage.svg';
import ClimateControlledIndoor from 'assets/images/psp/Icon/climate-controlled-indoor.svg';

import { storageCategories } from 'pages/PSPPage/utils/getPSPLinks';

const jetSkiContentPrepMarkdown =
	'Getting the [right storage unit size](https://www.selfstorage.com/blog/) is crucial for both protection and budget efficiency. Here`s what you need to know:\n\n' +
	'**Standard Jet Ski Dimensions:**\n\n' +
	'* Length: 10-12 feet (trailer adds 2-4 feet)\n' +
	'* Width: 3.5-4 feet\n' +
	'* Height: 3-4 feet (on trailer)\n\n' +
	'### Recommended Unit Sizes\n\n' +
	'**Solo Jet Ski Storage: 10x15 Unit**\n\n' +
	'> Perfect for one jet ski with trailer, plus storage space for life jackets, maintenance supplies, and accessories.\n\n' +
	'&nbsp;\n\n' +
	'**Multiple Watercraft: 10x20 Unit**\n\n' +
	'> Accommodates two jet skis or one jet ski plus additional water sports equipment.\n\n' +
	'&nbsp;\n\n' +
	'**Premium Storage with Workspace: 10x25 Unit**\n\n' +
	'> Extra room for maintenance, equipment storage, and easy maneuvering around your watercraft.\n\n' +
	'> Pro tip: Most jet skis are a little more than 10 ft. in length, so a 10x15 or larger drive-up storage unit should accommodate most personal watercraft. Remember to include your trailer length when measuring!\n\n' +
	'&nbsp;\n\n' +
	'**Jet Ski Storage Costs That Fit Your Budget**\n\n\n' +
	'> Smart storage consistently outperforms expensive alternatives. Building a jet ski garage addition costs $8,000-$25,000, while replacing a weather-damaged jet ski runs $8,000-$20,000. \n\n' +
	'> Professional storage protects your investment affordably with flexible month-to-month options.\n\n' +
	'> Whether you need seasonal winter storage or year-round protection, you only pay for what you actually use.\n\n';

const reviews = [
	{
		title: 'Storage for Jet skis',
		stars: 5,
		text: 'Very fast and simple to find storage on SelfStorage.com',
		customerName: 'Anthony Gutierrez',
		date: 'Jun 28, 2020',
		link: 'https://www.trustpilot.com/reviews/5ef7b2167dd75306b8cf59a6',
	},
	{
		title: 'This service is convenient and easy to use',
		stars: 5,
		text: 'The site provided the info I needed to find boat storage in my area at a reasonable rate. I was then able to reserve a space immediately before actually talking with anyone. This service is convenient and easy to use.',
		customerName: 'Steve K',
		date: 'Oct 17, 2021',
		link: 'https://www.trustpilot.com/reviews/616b6e80234f1b1fe55c3604',
	},
	{
		title: 'Accurate and Easy',
		stars: 5,
		text: 'The site helped locate the type of storage I wanted for my boat, and the after-hours (for the storage facility office) availability of the SelfStorage.com call center helped confirm a reservation once I located the best storage option. It was easy to navigate the site and easy to confirm reservations. The system generated the lead to the facility`s staff, who called the following business day to proceed with the rental.',
		customerName: 'Doug',
		date: 'Sep 28, 2020',
		link: 'https://www.trustpilot.com/reviews/5f721259798e6f09602220ae',
	},
	{
		title: 'Easiest part of my move',
		stars: 5,
		text: 'While moving from another state, I was able to set up the entire rental over the phone with SelfStorage.com before I arrived. I then drove straight to the site, dropped off my boat, and proceeded on my way without a problem.',
		customerName: 'Gary',
		date: 'Jul 9, 2020',
		link: 'https://www.trustpilot.com/reviews/5f06f4033f06f20a9c98eb82',
	},
];
const faqs = [
	{
		question: 'How do I prepare my jet ski for storage?',
		answer: 'Thoroughly clean inside and out, flush the cooling system, add fuel stabilizer, remove the battery, and apply a protective cover. Professional facilities often provide preparation checklists.',
	},
	{
		question: 'Can I store my jet ski without the trailer?',
		answer: 'Yes, but most owners prefer trailer storage for mobility and protection. If storing without a trailer, ensure proper support to prevent hull damage.',
	},
	{
		question: 'What size unit do I need for two jet skis?',
		answer: 'Most couples can fit two jet skis in a 10x20 unit. For extra maneuvering room or additional equipment storage, consider a 10x25 unit.',
	},
	{
		question: 'Is winter storage really necessary?',
		answer: 'In freezing climates, yes. Even a single freeze can crack your jet ski`s cooling system, leading to expensive repairs. Heated storage prevents this completely.',
	},
	{
		question: 'How often can I access my stored jet ski?',
		answer: 'Most facilities offer daily access during business hours, with many providing extended or 24/7 access for spontaneous water adventures.',
	},
	{
		question: 'Should I keep fuel in my jet ski during storage?',
		answer: 'Add fuel stabilizer and keep the tank nearly full to prevent condensation. Never store with an empty tank, as this can cause fuel system damage.',
	},
	{
		question: 'What accessories can I store with my jet ski?',
		answer: 'Most units accommodate life jackets, tow ropes, maintenance supplies, and even small water toys alongside your watercraft.',
	},
	{
		question: 'Do I need special insurance for stored jet skis?',
		answer: 'Check if your watercraft insurance covers off-site storage. Many facilities also offer storage insurance for additional protection.',
	},
];

export const JetskiContent = {
	type: 'Jet Ski',
	reviews: reviews,
	sections: [
		{
			title: 'Jetski Storage Hero',
			background: 'default',
			modules: [
				{
					type: 'hero',
					id: 'jet-ski-storage-hero',
					props: {
						content: {
							title: 'Find Affordable Jet Ski Self Storage',
							description:
								"For every ounce of excitement your jet ski brings to days down by the shore or out on the lake, there comes a lingering question: Where can I store my jet ski that won't take up half the garage or a chunk of the driveway?\n\n" +
								'The answer is closer than it may appear. Storing your jet ski in a storage unit is a smart, cost-effective solution to your jet ski storage woes. \n\n' +
								'Keeping your personal watercraft (PWC) safe, protected, and out of the way is simpler and more convenient than ever before — thanks to the flexible leasing terms, security, and affordability of jet ski storage units.\n\n' +
								'From seasonal winter storage to year-round indoor protection, find the perfect storage solution that keeps your jet ski ready for your next water adventure. **SelfStorage.com** makes finding and booking jet ski storage simple and stress-free',
							image: JetskiHero,
							CTAtext: 'Find Jet Ski Self Storage',
							storageType: 'JetSki',
						},
					},
				},
			],
		},

		{
			title: 'unit size chart',
			modules: [
				{
					type: 'cardStrip',
					props: {
						heading:
							'The Benefits of Storing Your Jet Ski in a Storage Unit',
						description: `If you have a family that loves watersports and adventure, you've likely got at least one jet ski taking up space in your [already overstuffed garage](https://www.selfstorage.com/blog/diy-garage-storage/), or parked in the corner of your ever-shrinking driveway.\n\nNo matter where you're keeping them, there's always the concern of theft, UV damage, exposure to the elements — or worse — your new driver accidentally bumping into them.\n\nStoring a jet ski in a storage unit rather than a DIY jet ski storage shed removes the HOA headaches from the equation. It offers more advanced protection from excess sunlight, extreme weather conditions, and the question of what to do with your PWC during the offseason.`,
						content:
							'Basic jet ski covers and driveway parking aren`t enough to protect your investment from these risks. Professional storage provides comprehensive protection at a fraction of the cost of building additional garage space.',
						cardData: [
							{
								content:
									'**Salt Water Damage**\n\n Corrosion destroys engines, trailers, and metal components \n\n → [Climate controlled storage units](https://www.selfstorage.com/blog/) with ample room to clean and prep your jet ski for long-term storage',
								imageSrc: SaltWater,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Home Space Shortage**\n\n' +
									'Most garages can`t fit jet skis and trailers alongside cars \n\n ' +
									'→ **Purpose-built units** designed specifically to protect PWCs and other vehicles',
								imageSrc: SpaceShortage,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Theft Vulnerability**\n\n' +
									'Jet skis are high-value, easily stolen targets in driveways \n\n' +
									'→ **Gated access** with surveillance and individual unit security systems',
								imageSrc: TheftVulnerability,
								contentAlign: 'text-left',
							},
							{
								content:
									'**UV and Weather Damage**\n\n' +
									'Sun exposure cracks seats and fades colors; rain causes rust \n\n' +
									'→ **Indoor climate control** shields from all weather and UV radiation',
								imageSrc: UVWeather,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Winter Freeze Risk**\n\n' +
									'Cold temperatures can crack cooling systems and damage engines \n\n' +
									'→ **Heated storage options** maintain safe temperatures year-round',
								imageSrc: WinterFreeze,
								contentAlign: 'text-left',
							},
							{
								content:
									'**Maintenance Hassles**\n\n' +
									'No convenient space for cleaning, [winterizing](https://www.selfstorage.com/blog/rv-winterization-guide/), or repairs \n\n' +
									'→ **Easy access units** with space for maintenance and equipment storage',
								imageSrc: Maintenance,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},

		{
			title: 'content section 2',
			background: 'blueWithMap',
			modules: [
				{
					type: 'content',
					props: {
						title: 'Essential Jet Ski Storage Sizing Guide',
						contentType: 'row-type',
						theme: 'dark',
						content: [jetSkiContentPrepMarkdown],
					},
				},
			],
		},
		{
			title: 'unit size chart 1',
			noPaddingBottom: true,
			modules: [
				{
					type: 'cardStrip',
					props: {
						content:
							'Pricing based on **SelfStorage.com** data. Small boats and jet skis can fit in 10x10 or 10x15 units, with costs varying by location and amenities.',
						cardData: [
							{
								title: 'Basic Outdoor Storage',
								content:
									'**Monthly Cost Range:** $50-$100*\n\n **Best for:** Budget-conscious seasonal storage\n\n **Key benefits:** Fenced security, affordable protection',
								imageSrc: BasicOutdoorStorage,
								contentAlign: 'text-left',
							},
							{
								title: 'Covered Storage',
								content:
									'**Monthly Cost Range:** $75-$125*\n\n **Best for:** UV protection, moderate weather shielding \n\n **Key benefits:** Roof coverage, ventilation, and easy access',
								imageSrc: CoveredStorage,
								contentAlign: 'text-left',
							},
							{
								title: 'Indoor Storage',
								content:
									'**Monthly Cost Range:** $100-$175* \n\n **Best for:** Complete protection, valuable watercraft \n\n **Key benefits:** Weather-proof, climate stability, maximum security',
								imageSrc: IndoorStorage,
								contentAlign: 'text-left',
							},
							{
								title: 'Climate-Controlled Indoor',
								content:
									'**Monthly Cost Range:** $125-$200* \n\n**Best for:** Premium jet skis, winter storage \n\n **Key benefits:** Temperature control, humidity management, freeze protection',
								imageSrc: ClimateControlledIndoor,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},

		{
			title: 'unit size chart 2',
			modules: [
				{
					type: 'cardStrip',
					props: {
						heading:
							'Why Storage Units Are Best for Jet Ski Storage',
						cardData: [
							{
								title: 'Specialized Security Features',
								content:
									'Advanced surveillance systems, controlled access, and personal watercraft expertise that general storage can`t match.',
								imageSrc: TheftVulnerability,
								contentAlign: 'text-left',
							},
							{
								title: 'Proper Ventilation Systems',
								content:
									'Professional facilities understand moisture control needs specific to watercraft storage, preventing mold and corrosion.',
								imageSrc: ProperVentilation,
								contentAlign: 'text-left',
							},
							{
								title: 'Convenient Maintenance Access',
								content:
									'Many locations offer wash stations, electrical outlets for battery maintenance, and space to work on your jet ski.',
								imageSrc: Maintenance,
								contentAlign: 'text-left',
							},
							{
								title: 'Insurance and Peace of Mind',
								content:
									'Professional facilities carry proper insurance and provide the documentation needed for your watercraft insurance claims.',
								imageSrc: InsurancePeace,
								contentAlign: 'text-left',
							},
						],
					},
				},
			],
		},

		{
			title: 'Reviews Section',
			background: 'lighterBlue',
			modules: [
				{
					type: 'reviews',
					slug: 'jet-ski-reviews',
					props: {
						reviewsData: {
							reviews,
							headerTitle: 'Jet Ski Self Storage: ',
						},
					},
				},
			],
		},
		{
			title: 'Faqs Section',
			background: 'white',
			modules: [
				{
					type: 'faqs',
					props: {
						content: {
							header: 'Jet Ski Self Storage FAQs',
							faqs,
						},
					},
				},
			],
		},
		{
			title: 'featured content',
			background: 'darkBlue',
			modules: [
				{
					type: 'featuredContent',
					props: {
						data: {
							heading: 'Learn All About Jet Ski Self Storage',
							type: 'unitCards',
							id: 'storage size unit cards',
							data: {
								slug: 'storage-size-guide-unit-cards',
								unitCards: [
									{
										title: 'Vehicle Storage',
										url: 'https://www.selfstorage.com/blog/small-vehicle-storage-guide/',
										layout: false,
										plain: true,
										date: 'October 16, 2025',
										image: 'https://www.selfstorage.com/blog/wp-content/uploads/2025/10/motorcycle.png',
										content:
											'Small Vehicle Storage: How to Store Motorcycles, ATVs, and Golf Carts ',
									},
									{
										title: 'Vehicle Storage',
										url: 'https://www.selfstorage.com/blog/how-to-store-a-boat-2',
										layout: false,
										plain: true,
										date: 'July 23, 2025',
										image: 'https://www.selfstorage.com/blog/wp-content/uploads/2022/07/dry-stack-boat-storage.jpg',
										content:
											'Best Way to Store a Boat During Off-Season',
									},
									{
										title: 'Storage Tips',
										url: 'https://www.selfstorage.com/blog/outdoor-gear-shed-storage-unit/',
										layout: false,
										plain: true,
										date: 'October 16, 2025',
										image: 'https://www.selfstorage.com/blog/wp-content/uploads/2025/05/shutterstock_209402077-scaled.jpg',
										content:
											'Sports Equipment Storage Guide: How to Create A Sports and Outdoor Gear Storage Room',
									},
									{
										title: 'Storage Tips',
										url: 'https://www.selfstorage.com/blog/preparing-hiking-equipment-for-storage/',
										layout: false,
										plain: true,
										date: 'May 14, 2025',
										image: 'https://www.selfstorage.com/blog/wp-content/uploads/2025/04/shutterstock_1548283061-scaled.jpg',
										content:
											'How to Prepare & Store Hiking and Camping Gear | Storage Guide',
									},
								],
							},
						},
					},
				},
			],
		},
		{
			title: 'Links to other PSPs',
			background: 'lighterBlue',
			modules: [
				{
					type: 'links',
					props: {
						heading: {
							headingName: 'More Ways to Store',
						},
						linksList: storageCategories.jetSki,
					},
				},
			],
		},

		{
			title: 'nearby units',
			modules: [
				{
					type: 'storageNearYou',
					props: {
						type: 'Jet Ski',
					},
				},
			],
		},
	],
};
